/**
 * CREATIVE DETONATOR - Navigation Module
 * Handles phase transitions and prevents circular dependencies
 *
 * WHY THIS EXISTS:
 * - generator.js (orchestrator) imports phase modules
 * - phase modules need to call goToPhase()
 * - If goToPhase() lived in generator.js, we'd have a circular import
 * - This module is imported by BOTH, solving the problem
 *
 * This module contains:
 * - goToPhase() - Navigate to a specific phase with initialization
 * - goToPhaseDirectly() - Navigate without triggering detonation (for dev mode)
 * - resetGenerator() - Reset to initial state (for "New Session" button)
 * - registerPhaseInit() - Register phase initialization callbacks
 */

// v61
import { GeneratorState, elements } from './state.js';

// ============================================================================
// PHASE MANAGER - Registration System
// ============================================================================

/**
 * Phase initialization function registry
 * Populated by generator.js during startup
 */
const PhaseManager = {
  initFunctions: {}
};

/**
 * Register a phase initialization function
 * Called by generator.js during DOMContentLoaded
 * @param {number} phaseNum - Phase number (1-4)
 * @param {Function} initFn - Initialization function for that phase
 */
export function registerPhaseInit(phaseNum, initFn) {
  PhaseManager.initFunctions[phaseNum] = initFn;
}

// ============================================================================
// PHASE NAVIGATION
// ============================================================================

/**
 * Navigate to a specific phase
 * Updates UI indicators, shows correct phase, and calls init function
 * @param {number} phaseNumber - Target phase (1-4)
 */
export function goToPhase(phaseNumber) {
  GeneratorState.phase = phaseNumber;

  // Query DOM elements directly (avoids elements cache race condition)
  const steps = document.querySelectorAll('.phase-step');
  const lines = document.querySelectorAll('.phase-line');
  const phases = document.querySelectorAll('.generator-phase');

  // Update phase step indicators (dots inside step buttons)
  steps.forEach((step, i) => {
    const dot = step.querySelector('.phase-dot');
    if (dot) {
      dot.classList.remove('active', 'completed');
      if (i + 1 < phaseNumber) {
        dot.classList.add('completed');
      } else if (i + 1 === phaseNumber) {
        dot.classList.add('active');
      }
    }
  });

  // Update phase lines
  lines.forEach((line, i) => {
    line.classList.toggle('active', i + 1 < phaseNumber);
  });

  // Show correct phase section
  phases.forEach((phase, i) => {
    phase.classList.remove('active');
    if (i + 1 === phaseNumber) {
      phase.classList.add('active');
    }
  });

  // Call the registered init function for this phase
  if (PhaseManager.initFunctions[phaseNumber]) {
    PhaseManager.initFunctions[phaseNumber]();
  }

  // Scroll to top
  window.scrollTo(0, 0);
}

/**
 * Navigate directly to phase without triggering init
 * Used by dev mode for jumping to phases (avoids running detonation)
 * @param {number} phaseNumber - Target phase (1-4)
 */
export function goToPhaseDirectly(phaseNumber) {
  GeneratorState.phase = phaseNumber;

  const steps = document.querySelectorAll('.phase-step');
  const lines = document.querySelectorAll('.phase-line');
  const phases = document.querySelectorAll('.generator-phase');

  // Update phase step indicators
  steps.forEach((step, i) => {
    const dot = step.querySelector('.phase-dot');
    if (dot) {
      dot.classList.remove('active', 'completed');
      if (i + 1 < phaseNumber) {
        dot.classList.add('completed');
      } else if (i + 1 === phaseNumber) {
        dot.classList.add('active');
      }
    }
  });

  // Update phase lines
  lines.forEach((line, i) => {
    line.classList.toggle('active', i + 1 < phaseNumber);
  });

  // Show correct phase
  phases.forEach((phase, i) => {
    phase.classList.remove('active');
    if (i + 1 === phaseNumber) {
      phase.classList.add('active');
    }
  });

  // Initialize phase-specific functionality (without triggering detonation)
  // Note: Phase 3 (detonation) is explicitly skipped here
  switch (phaseNumber) {
    case 2:
      if (PhaseManager.initFunctions[2]) {
        PhaseManager.initFunctions[2]();
      }
      break;
    case 4:
      if (PhaseManager.initFunctions[4]) {
        PhaseManager.initFunctions[4]();
      }
      break;
  }
}

// ============================================================================
// RESET FUNCTIONALITY
// ============================================================================

/**
 * Reset the generator to initial state
 * Used by "New Session" button in Phase 4
 * Lives here to avoid circular dependency with phase4-harvest.js
 */
export function resetGenerator() {
  // 1. Reset State
  GeneratorState.phase = 1;
  GeneratorState.brief = { product: '', benefit: '', formats: [] };
  GeneratorState.selectedTriggers = [];
  GeneratorState.generatedIdeas = [];
  GeneratorState.selectedIdeas = [];
  GeneratorState.savedIdeas = [];

  // 2. Reset UI (Inputs) - use elements from state.js import
  if (elements.inputProduct) elements.inputProduct.value = '';
  if (elements.inputBenefit) elements.inputBenefit.value = '';

  // 3. Reset Format Card Classes
  if (elements.formatCards) {
    elements.formatCards.forEach(c => c.classList.remove('selected'));
  }
  if (elements.btnToArming) elements.btnToArming.disabled = true;

  // 4. Reset selection count
  if (elements.selectionCount) elements.selectionCount.textContent = '0';

  // 5. Reset Phase Indicators (Visuals)
  const steps = document.querySelectorAll('.phase-step');
  const lines = document.querySelectorAll('.phase-line');

  steps.forEach((step, i) => {
    const dot = step.querySelector('.phase-dot');
    if (dot) {
      dot.classList.remove('active', 'completed');
      if (i === 0) dot.classList.add('active');
    }
  });
  lines.forEach(line => line.classList.remove('active'));

  // 6. Reset questions to first question
  if (elements.questionScreens) {
    elements.questionScreens.forEach(screen => screen.classList.remove('active'));
  }
  document.getElementById('q1')?.classList.add('active');

  // 7. Reset ALL dev nav buttons across all navs
  document.querySelectorAll('.dev-nav-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.question === 'q1');
  });

  // 8. Reset ALL dev nav values across all navs
  document.querySelectorAll('#nav-product-value, .nav-product-value').forEach(el => el.textContent = '');
  document.querySelectorAll('#nav-benefit-value, .nav-benefit-value').forEach(el => el.textContent = '');
  document.querySelectorAll('#nav-format-value, .nav-format-value').forEach(el => el.textContent = '');

  // 9. Show Phase 1
  document.querySelectorAll('.generator-phase').forEach((phase, i) => {
    phase.classList.remove('active');
    if (i === 0) phase.classList.add('active');
  });
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for legacy compatibility and HTML onclick handlers
window.goToPhase = goToPhase;
window.goToPhaseDirectly = goToPhaseDirectly;
window.resetGenerator = resetGenerator;
